import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import RoleAvatar from '@rsos/components/capstone/RolesShared/RoleAvatar';
import { trackRolePageAccess } from '@rsos/components/capstone/RolesShared/rolesTrackings';
import { dividePermissionArrayByType } from '@rsos/components/capstone/RolesShared/utils/dividePermissionArrayByType';
import { getAvatarBg } from '@rsos/components/capstone/RolesShared/utils/getAvatarBg';
import { Box, Accordion, Text, Button } from '@rsos/components/capstone/base';
import {
  updateAppRole,
  selectUserOrgByApp,
  createAppRole,
} from '@rsos/sinatra';
import {
  checkPremiumPermissions,
  notifyMissingRequiredPermissions,
} from '@rsos/utils/checkPremiumPermissions';
import { notifySuccess, notifyError } from '@rsos/utils/toastUtils';
import HeaderRoleDuplicateInProgress from './HeaderRoleDuplicateInProgress';
import PermissionAccordionContent from './PermissionAccordionContent';
import {
  RoleContainer,
  RoleHeader,
  AccordionSectionTitle,
  AccordionSectionDesc,
} from './Role.styles';
import RoleActionControl from './RoleActionControl';

const Role = props => {
  const {
    role,
    onDuplicate,
    isDuplicateInProgress,
    duplicateComplete,
    isMaxRolesCountReached,
    permissions,
  } = props;
  const { permissions_full, name, avatar_bg_color } = role;

  const schema = Yup.object().shape({
    name: Yup.string()
      .required('The Role Title is required')
      .max(40, 'The title is too long'),
  });

  const [pageAccessPermissions, dataSourcesPermissions] =
    dividePermissionArrayByType(permissions_full, e => e.rsp_rbac); //NOTE: permissions that belong to this role
  const [allPageAccessPermissions, allDataSourcesPermissions] =
    dividePermissionArrayByType(permissions, e => e.rsp_rbac); //NOTE: all permissions

  // Sort Features and Data Sources alphabetically by display_name
  const sortedAllPageAccessPermissions = [...allPageAccessPermissions].sort(
    (a, b) => a.display_name.localeCompare(b.display_name),
  );
  const sortedAllDataSourcesPermissions = [...allDataSourcesPermissions].sort(
    (a, b) => a.display_name.localeCompare(b.display_name),
  );

  const [localPageAccessPermissions] = useState(pageAccessPermissions);
  const [localDataSourcePermissions] = useState(dataSourcesPermissions);
  const [isSubmitEnabled, setIsSubmitEnabled] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(
    allDataSourcesPermissions.length === dataSourcesPermissions.length,
  );

  const sinatra = useSelector(state => state.sinatra);
  const { irpVersion } = useSelector(state => state.sinatra.irp);

  const dispatch = useDispatch();
  const { application } = sinatra.user.currentRole;
  const org = selectUserOrgByApp(sinatra, application);

  const handleDeselectAll = (setValues, values) => {
    setIsSelectAll(false);
    setIsSubmitEnabled(true);
    setValues({
      ...values,
      ...{ dataSourcesPermissions: [] },
    });
  };
  const handleSelectAll = (setValues, values) => {
    setIsSelectAll(true);
    setIsSubmitEnabled(true);
    setValues({
      ...values,
      ...{
        dataSourcesPermissions: allDataSourcesPermissions.map(i => i.name),
      },
    });
  };
  const handleUpdateRole = values => {
    // TODO: potential improvement to diff the intial object with what has changed to send a leaner payload
    const payload = {
      name: role.name,
      id: role.id,
      permissions: [
        ...values.pageAccessPermissions,
        ...values.dataSourcesPermissions,
      ],
    };

    const missingRequiredPermissions = checkPremiumPermissions({
      assignedPermissions: payload.permissions,
    });

    if (missingRequiredPermissions) {
      notifyMissingRequiredPermissions();
      return;
    }

    dispatch(updateAppRole(org.id, application, payload))
      .then(() => {
        setIsSubmitEnabled(false);
        notifySuccess('Role updated.');
        trackRolePageAccess(
          role.name,
          values.pageAccessPermissions,
          irpVersion,
        );
      })
      .catch(action => {
        const { error } = action;
        notifyError(error.response.data.detail);
      });
  };
  const handleCheckboxChange = (e, arrayHelpers, permission, values) => {
    setIsSubmitEnabled(true); // simple handling now, doesn't include uncheck/check same value

    if (e.target.checked) {
      arrayHelpers.push(permission.name);
      // check if the isSelectAll should be true or false
      const isEqual =
        values.indexOf(permission.name) === -1
          ? [...values, permission.name].length ===
            allDataSourcesPermissions.length
          : values.length === allDataSourcesPermissions.length;
      setIsSelectAll(isEqual ? true : false);
    } else {
      setIsSelectAll(false);
      const idx = values.indexOf(permission.name);
      arrayHelpers.remove(idx);
    }
  };
  const handleDuplicateRole = values => {
    const payload = {
      permissions: [
        ...values.pageAccessPermissions,
        ...values.dataSourcesPermissions,
      ],
      name: values.name,
      avatar_bg_color: getAvatarBg(),
    };
    dispatch(createAppRole(org.id, application, payload))
      .then(() => {
        notifySuccess('Role successfully duplicated and added.');
        duplicateComplete();
      })
      .catch(action => {
        const { error } = action;
        notifyError(error.response.data.detail);
      });
  };
  const initialValues = {
    pageAccessPermissions: localPageAccessPermissions.map(i => i.name),
    dataSourcesPermissions: localDataSourcePermissions.map(i => i.name),
    name: name,
  };
  return (
    <RoleContainer>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={async values => {
          isDuplicateInProgress
            ? handleDuplicateRole(values)
            : handleUpdateRole(values);
        }}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          errors,
          setValues,
        }) => (
          <Form>
            {isDuplicateInProgress && role.isBeingDuplicated ? (
              <HeaderRoleDuplicateInProgress
                role={role}
                duplicateComplete={duplicateComplete}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setFieldValue={setFieldValue}
                values={values}
                error={errors.name}
              />
            ) : (
              <RoleHeader>
                <Box horizontal flow={7} justify="center" align="center">
                  <RoleAvatar name={name} color={avatar_bg_color} />
                  <Text fontWeight="bold" color="primaryText">
                    {name}
                  </Text>
                </Box>
                <Box horizontal flow={5}>
                  <Button
                    btnType={isSubmitEnabled ? 'primary' : 'tertiary'}
                    size="xsmall"
                    type="submit"
                    disabled={!isSubmitEnabled}
                  >
                    Save
                  </Button>
                  <RoleActionControl
                    onDuplicate={onDuplicate}
                    role={role}
                    isMaxRolesCountReached={isMaxRolesCountReached}
                    isDuplicateInProgress={isDuplicateInProgress}
                  />
                </Box>
              </RoleHeader>
            )}
            <Accordion
              header={
                <Box flow={5}>
                  <AccordionSectionTitle>Features</AccordionSectionTitle>
                  <AccordionSectionDesc>
                    This role has access to the following
                  </AccordionSectionDesc>
                </Box>
              }
              interactionType="button"
              height={250}
              isOpened={isDuplicateInProgress}
              width="100%"
              content={
                <PermissionAccordionContent
                  allPermissions={sortedAllPageAccessPermissions}
                  permissions={pageAccessPermissions}
                  type="pageAccessPermissions"
                  onCheckBoxChange={handleCheckboxChange}
                  values={values.pageAccessPermissions}
                />
              }
            ></Accordion>
            <Accordion
              stacked
              header={
                <Box flow={5}>
                  <AccordionSectionTitle>Data Sources</AccordionSectionTitle>
                  <AccordionSectionDesc>
                    This role has access to the following
                  </AccordionSectionDesc>
                </Box>
              }
              interactionType="button"
              height={250}
              width="100%"
              content={
                <PermissionAccordionContent
                  allPermissions={sortedAllDataSourcesPermissions}
                  permissions={dataSourcesPermissions}
                  type="dataSourcesPermissions"
                  onCheckBoxChange={handleCheckboxChange}
                  values={values.dataSourcesPermissions}
                  deselectAll={() => handleDeselectAll(setValues, values)}
                  selectAll={() => handleSelectAll(setValues, values)}
                  isSelectAll={isSelectAll}
                />
              }
            ></Accordion>
          </Form>
        )}
      </Formik>
    </RoleContainer>
  );
};

export default Role;
